import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { Assessment_Activity_Bool_Exp } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  type Action_Bool_Exp,
  type Ancestor_Contributor_Bool_Exp,
  type Assessment_Bool_Exp,
  type Control_Bool_Exp,
  type Document_Bool_Exp,
  type Indicator_Bool_Exp,
  type Internal_Audit_Entity_Bool_Exp,
  type Issue_Bool_Exp,
  type Obligation_Bool_Exp,
  Parent_Type_Enum,
  type Risk_Bool_Exp,
  type Third_Party_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

import { resolveEffectiveTarget } from '../../targetUtils';
import type { MyItemsFilter, MyItemsTarget } from '../../useDashboardStore';
import { useDashboardStore } from '../../useDashboardStore';

export const getQueryVariables = (
  {
    owner,
    contributor,
    groupOwner,
    groupContributor,
    inheritedOwner,
    inheritedContributor,
    inheritedGroupOwner,
    inheritedGroupContributor,
  }: MyItemsFilter,
  userId?: string,
  target?: MyItemsTarget | null
) => {
  // When a target is selected the dashboard rescopes to that user/group:
  //   - user target: swap userId for target.id; group conditions still match
  //     on UserId via the group membership join, so they catch items where
  //     the target user is in an owner/contributor group.
  //   - group target: filter ownerGroups/contributorGroups by UserGroupId directly;
  //     short-circuit the user-relationship flags (owner/contributor) because
  //     a group can't be a direct owner/contributor in those tables.
  const { effectiveUserId, isTargetGroup, targetGroupId } =
    resolveEffectiveTarget(target, userId);

  // For a user target the contributor edge can be direct (UserGroupId null) or
  // via a group (UserGroupId set) — `isNull` picks the variant. For a group
  // target the edge is always via the group, so the `_is_null` clause is
  // skipped and we match the group id directly.
  const getAncestorContributorsConditions = (
    contributorType: 'contributor' | 'owner',
    isNull: boolean
  ): Ancestor_Contributor_Bool_Exp =>
    isTargetGroup
      ? {
          _and: [
            { UserGroupId: { _eq: targetGroupId } },
            { ContributorType: { _eq: contributorType } },
          ],
        }
      : {
          _and: [
            { UserId: { _eq: effectiveUserId } },
            { ContributorType: { _eq: contributorType } },
            { UserGroupId: { _is_null: isNull } },
          ],
        };

  const itemFilterConditions: Risk_Bool_Exp = { _or: [] };
  // assessment activities are only filtered by owners and ownerGroups
  const assessmentActivityFilterConditions: Assessment_Activity_Bool_Exp = {
    _or: [],
  };

  if (owner && !isTargetGroup) {
    itemFilterConditions._or?.push({
      owners: { UserId: { _eq: effectiveUserId } },
    });

    assessmentActivityFilterConditions._or?.push({
      owners: { UserId: { _eq: effectiveUserId } },
    });
  }

  if (contributor && !isTargetGroup) {
    itemFilterConditions._or?.push({
      contributors: { UserId: { _eq: effectiveUserId } },
    });
  }

  if (groupOwner) {
    if (isTargetGroup) {
      itemFilterConditions._or?.push({
        ownerGroups: { UserGroupId: { _eq: targetGroupId } },
      });
      assessmentActivityFilterConditions._or?.push({
        ownerGroups: { UserGroupId: { _eq: targetGroupId } },
      });
    } else {
      itemFilterConditions._or?.push({
        ownerGroups: {
          group: { users: { UserId: { _eq: effectiveUserId } } },
        },
      });
      assessmentActivityFilterConditions._or?.push({
        ownerGroups: {
          group: { users: { UserId: { _eq: effectiveUserId } } },
        },
      });
    }
  }

  if (groupContributor) {
    if (isTargetGroup) {
      itemFilterConditions._or?.push({
        contributorGroups: { UserGroupId: { _eq: targetGroupId } },
      });
    } else {
      itemFilterConditions._or?.push({
        contributorGroups: {
          group: { users: { UserId: { _eq: effectiveUserId } } },
        },
      });
    }
  }

  if (inheritedOwner && !isTargetGroup) {
    itemFilterConditions._or?.push({
      _and: [
        { _not: { owners: { UserId: { _eq: effectiveUserId } } } },
        {
          ancestorContributors: getAncestorContributorsConditions(
            'owner',
            true
          ),
        },
      ],
    });
  }

  if (inheritedGroupOwner) {
    const notDirectGroupOwner: Risk_Bool_Exp = isTargetGroup
      ? { _not: { ownerGroups: { UserGroupId: { _eq: targetGroupId } } } }
      : {
          _not: {
            ownerGroups: {
              group: { users: { UserId: { _eq: effectiveUserId } } },
            },
          },
        };
    itemFilterConditions._or?.push({
      _and: [
        notDirectGroupOwner,
        {
          ancestorContributors: getAncestorContributorsConditions(
            'owner',
            false
          ),
        },
      ],
    });
  }

  if (inheritedContributor && !isTargetGroup) {
    itemFilterConditions._or?.push({
      _and: [
        {
          _not: { contributors: { UserId: { _eq: effectiveUserId } } },
        },
        {
          ancestorContributors: getAncestorContributorsConditions(
            'contributor',
            true
          ),
        },
      ],
    });
  }

  if (inheritedGroupContributor) {
    const notDirectGroupContributor: Risk_Bool_Exp = isTargetGroup
      ? { _not: { contributorGroups: { UserGroupId: { _eq: targetGroupId } } } }
      : {
          _not: {
            contributorGroups: {
              group: { users: { UserId: { _eq: effectiveUserId } } },
            },
          },
        };
    itemFilterConditions._or?.push({
      _and: [
        notDirectGroupContributor,
        {
          ancestorContributors: getAncestorContributorsConditions(
            'contributor',
            false
          ),
        },
      ],
    });
  }

  return {
    userId: effectiveUserId ?? '',
    riskFilterConditions: itemFilterConditions,
    actionFilterConditions: itemFilterConditions as Action_Bool_Exp,
    indicatorFilterConditions: itemFilterConditions as Indicator_Bool_Exp,
    documentFilterConditions: itemFilterConditions as Document_Bool_Exp,
    assessmentFilterConditions: itemFilterConditions as Assessment_Bool_Exp,
    issueFilterConditions: itemFilterConditions as Issue_Bool_Exp,
    internalAuditFilterConditions:
      itemFilterConditions as Internal_Audit_Entity_Bool_Exp,
    obligationFilterConditions: itemFilterConditions as Obligation_Bool_Exp,
    thirdPartyFilterConditions: itemFilterConditions as Third_Party_Bool_Exp,
    controlFilterConditions: itemFilterConditions as Control_Bool_Exp,
    assessmentActivityFilterConditions,
  };
};

/**
 * Builds up the gql filtering conditions based on the current filters set on the dashboard,
 * as well as any global entity filters that are set
 * @returns getQueryVariables function
 */
export const useGetQueryVariables = () => {
  const { myItemsFilters, myItemsTarget } = useDashboardStore();
  const { user } = useRisksmartUser();

  const vars = useMemo(
    () => getQueryVariables(myItemsFilters, user!.userId, myItemsTarget),
    [myItemsFilters, myItemsTarget, user]
  );

  return {
    ...vars,
    riskFilterConditions: useEntityWhereFilter<Risk_Bool_Exp>(
      Parent_Type_Enum.Risk,
      vars.riskFilterConditions
    ),
    actionFilterConditions: useEntityWhereFilter<Action_Bool_Exp>(
      Parent_Type_Enum.Action,
      vars.actionFilterConditions
    ),
    indicatorFilterConditions: useEntityWhereFilter<Indicator_Bool_Exp>(
      Parent_Type_Enum.Indicator,
      vars.indicatorFilterConditions
    ),
    documentFilterConditions: useEntityWhereFilter<Document_Bool_Exp>(
      Parent_Type_Enum.Document,
      vars.documentFilterConditions
    ),
    assessmentFilterConditions: useEntityWhereFilter<Assessment_Bool_Exp>(
      Parent_Type_Enum.Assessment,
      vars.assessmentFilterConditions
    ),
    issueFilterConditions: useEntityWhereFilter<Issue_Bool_Exp>(
      Parent_Type_Enum.Issue,
      vars.issueFilterConditions
    ),
    internalAuditFilterConditions:
      useEntityWhereFilter<Internal_Audit_Entity_Bool_Exp>(
        Parent_Type_Enum.InternalAuditEntity,
        vars.internalAuditFilterConditions
      ),
    obligationFilterConditions: useEntityWhereFilter<Obligation_Bool_Exp>(
      Parent_Type_Enum.Obligation,
      vars.obligationFilterConditions
    ),
    thirdPartyFilterConditions: useEntityWhereFilter<Third_Party_Bool_Exp>(
      Parent_Type_Enum.ThirdParty,
      vars.thirdPartyFilterConditions
    ),
    controlFilterConditions: useEntityWhereFilter<Control_Bool_Exp>(
      Parent_Type_Enum.Control,
      vars.controlFilterConditions
    ),
  };
};
